import { useCallback, useEffect, useState } from 'react';
import { useLlcApi } from '../Api';

const LLC_MAINTENANCE_SERVICE_TYPE_NAME = 'LLC Maintenance';

interface ServiceRequestTypeDto {
  id: number;
  name: string;
  description?: string | null;
}

interface LlcMaintenanceServiceTypeIdState {
  serviceRequestTypeId: number | null;
  loading: boolean;
  error: boolean;
}

// Resolves the numeric id by name via the LLC API's catalog, rather than
// hardcoding it, so a backend renumbering doesn't silently break this link.
export function useLlcMaintenanceServiceTypeId(): LlcMaintenanceServiceTypeIdState {
  const llcApi = useLlcApi();
  const [state, setState] = useState<LlcMaintenanceServiceTypeIdState>({
    serviceRequestTypeId: null,
    loading: true,
    error: false,
  });

  const load = useCallback(
    async (signal: AbortSignal) => {
      setState((s) => ({ ...s, loading: true, error: false }));
      try {
        const response = await llcApi.get<ServiceRequestTypeDto[]>(
          '/api/ServiceRequestTypes',
          { signal }
        );
        const match = (response.data ?? []).find(
          (t) => t.name === LLC_MAINTENANCE_SERVICE_TYPE_NAME
        );
        setState({
          serviceRequestTypeId: match?.id ?? null,
          loading: false,
          error: false,
        });
      } catch {
        if (signal.aborted) return;
        setState({ serviceRequestTypeId: null, loading: false, error: true });
      }
    },
    [llcApi]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  return state;
}
